import { useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import type { Template } from '@/lib/types';
import TemplateEditor from './TemplateEditor';

export default function Templates() {
  return (
    <Routes>
      <Route index element={<TemplatesList />} />
      <Route path="new" element={<TemplateEditor />} />
      <Route path=":id" element={<TemplateEditor />} />
    </Routes>
  );
}

function TemplatesList() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<{ ok: true; items: Template[] }>('/api/templates'),
  });

  const seed = useMutation({
    mutationFn: () => api<{ ok: true; created: number; skipped: boolean }>('/api/templates/seed-defaults', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });

  const items = query.data?.items || [];

  return (
    <div className="space-y-4 max-w-4xl">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Šablony dopisů</h1>
        <Link
          to="/templates/new"
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium"
        >
          + Nová šablona
        </Link>
      </header>

      <p className="text-sm text-muted-foreground">
        Šablony jsou plain text s placeholdery (např. <code className="text-xs bg-muted px-1 rounded">{'{majitel_oslovení} {majitel_prijmeni}'}</code>).
        Vkládat můžeš vlastní text — systém ti pomůže s automatickou detekcí proměnných.
      </p>

      {query.isLoading && <p className="text-sm text-muted-foreground">Načítám…</p>}

      {items.length === 0 && !query.isLoading && (
        <div className="rounded-lg border border-dashed p-8 text-center space-y-3">
          <div className="text-sm text-muted-foreground">
            Zatím žádné šablony. Můžeš začít od nuly, nebo si nech přidat tři výchozí
            (obecná, taktní pro distress, follow-up).
          </div>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => seed.mutate()}
              disabled={seed.isPending}
              className="px-3 py-1.5 rounded-md border text-sm hover:bg-muted disabled:opacity-50"
            >
              {seed.isPending ? 'Vytvářím…' : 'Naplnit ukázkovými'}
            </button>
            <Link
              to="/templates/new"
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium"
            >
              Začít vlastní
            </Link>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <ul className="rounded-lg border divide-y">
          {items.map((t) => (
            <TemplateRow key={t.id} t={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TemplateRow({ t }: { t: Template }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/api/templates/${t.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });

  return (
    <li className="p-4 hover:bg-muted/30 flex items-start gap-3">
      <Link to={`/templates/${t.id}`} className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{t.name}</span>
          {t.isDefault && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
              výchozí
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap font-mono">
          {t.body.split('\n').slice(0, 3).join(' · ')}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {t.body.length} znaků · upraveno {new Date(t.updatedAt).toLocaleDateString('cs-CZ')}
        </div>
      </Link>
      <div className="shrink-0 flex items-center gap-2">
        {confirming ? (
          <>
            <span className="text-xs text-destructive">Smazat?</span>
            <button
              onClick={() => del.mutate()}
              disabled={del.isPending}
              className="px-2 py-1 rounded bg-destructive text-destructive-foreground text-xs font-medium disabled:opacity-50"
            >
              Ano
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="px-2 py-1 rounded border text-xs hover:bg-muted"
            >
              Ne
            </button>
          </>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
            title="Smazat"
          >
            <Trash size={14} />
          </button>
        )}
      </div>
    </li>
  );
}
